import React from 'react';
import { Brain, Code, Database, Cloud, Shield, Users, ArrowRight } from 'lucide-react';
import { services } from '../mockData';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useNavigate } from 'react-router-dom';

const iconMap = {
  brain: Brain,
  code: Code,
  database: Database,
  cloud: Cloud,
  shield: Shield,
  users: Users
};

const Services = () => {
  const navigate = useNavigate();

  const handleServiceClick = (slug) => {
    navigate(`/services/${slug}`);
  };

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#1A3E6E] font-semibold text-sm uppercase tracking-wide">
            What We Offer
          </span>
          <h2 className="mt-4 text-4xl lg:text-5xl font-bold text-gray-900">
            AI-Powered Software Solutions
          </h2>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            From artificial intelligence to full-stack development, we provide comprehensive 
            technology services tailored to your business needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card 
                key={service.id}
                className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-[#1A3E6E] overflow-hidden cursor-pointer"
                onClick={() => handleServiceClick(service.slug)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="bg-[#1A3E6E] p-3 rounded-lg">
                      <IconComponent className="text-white" size={24} />
                    </div>
                  </div>
                </div>
                
                <CardHeader>
                  <CardTitle className="text-2xl group-hover:text-[#1A3E6E] transition-colors">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-gray-600 text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-2 mb-6">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 bg-[#1A3E6E] rounded-full"></div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    className="text-[#1A3E6E] hover:text-[#1A3E6E] hover:bg-orange-50 p-0 h-auto font-semibold group/btn"
                  >
                    Learn More
                    <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" size={16} />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
